import type React from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import type { FieldRenderProps } from '@/js/libs/final-form';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { DatePicker } from '@/js/libs/ui';

import Error from 'src/components/form/Error/index.tsx';
import { useTranslationCallbackRef } from 'src/hooks/useTranslationCallbackRef.ts';

const Wrapper = styled.div`
  display: flex;
  & > * {
    flex: 1 1 auto;
  }
  & > * + * {
    margin-left: 10px;
  }
`;

export type DateRange = {
  start: Date | null;
  end: Date | null;
};

export type ValidDateRange = {
  start: Date;
  end: Date;
};

type DateRangeFieldProps = FieldRenderProps<DateRange | null, HTMLInputElement>;

export const DateRangeField: React.FC<DateRangeFieldProps> = ({ input, className }) => {
  const { onChange, value, name, ...restInput } = input;
  const { t } = useTranslation(['backend', 'common']);

  const handleStartDateChange = useCallbackRef((start: Date) => onChange({ ...value, start }));
  const handleEndDateChange = useCallbackRef((end: Date) => onChange({ ...value, end }));

  const blurInput = useCallbackRef(() => {
    input.onBlur();
  });

  const handleStartDateSelect = useCallbackRef((startDate: Date) => {
    if (startDate) {
      document.getElementById(`${name}-date-end`)?.focus();
    } else {
      blurInput();
    }
  });

  return (
    <Wrapper className={className}>
      <div data-testid='start-date'>
        <DatePicker
          id={`${name}-date-start`}
          selected={value?.start}
          onChange={handleStartDateChange}
          onSelect={handleStartDateSelect}
          {...restInput}
          startDate={value?.start}
          endDate={value?.end}
          placeholder={t('common:START_DATE')}
          selectsStart
          disabledKeyboardNavigation
          showWeekNumbers
        />
        <Error name={`${name}-start`} />
      </div>
      <div data-testid='end-date'>
        <DatePicker
          id={`${name}-date-end`}
          selected={value?.end}
          placeholder={t('common:END_DATE')}
          onChange={handleEndDateChange}
          onSelect={blurInput}
          {...restInput}
          startDate={value?.start}
          endDate={value?.end}
          selectsEnd
          disabledKeyboardNavigation
          showWeekNumbers
        />
        <Error name={`${name}-end`} />
      </div>
    </Wrapper>
  );
};

export const useDateRangeValidator = () => {
  const [t] = useTranslation();

  return useTranslationCallbackRef((fieldName: string, range?: DateRange): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!range) {
      return { [fieldName]: t('messages:FIELD_IS_REQUIRED') };
    }
    if (!range.start) {
      errors[`${fieldName}-start`] = t('messages:START_DATE_IS_REQUIRED');
    }
    if (!range.end) {
      errors[`${fieldName}-end`] = t('messages:END_DATE_IS_REQUIRED');
    }
    if (range.start && range.end && range.start.getTime() > range.end.getTime()) {
      errors[fieldName] = t('messages:START_DATE_MUST_BE_BEFORE_END_DATE');
    }
    return errors;
  }, t);
};
